import { Skeleton } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ActionPlanService } from "../../api/ActionPlanService";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import {
  ActionPlanPreferences,
  ActivityResponse,
  FocusArea,
} from "../../types/actionPlan";
import { ActionPlanSetupBanner } from "../ActionPlanSetupBanner/ActionPlanSetupBanner";
import { Button } from "../ui/Button/Button";
import { Card } from "../ui/Card/Card";
import { ProgressBar } from "../ui/ProgressBar/ProgressBar";
import { Typography } from "../ui/Typography/Typography";
import { ActionPlanOverviewStep } from "./ActionPlanOverviewStep";
import { PreferenceStep } from "./PreferenceStep";
import {
  getDeliveryIntervalOptions,
  getFocusAreaOptions,
  getInclusionPulseOptions,
} from "./actionPlanSetup.utils";
import "./ActionPlanSetupSteps.scss";

const STEPS = {
  INTRO: 0,
  INCLUSION_PULSE: 1,
  TRAINING_INTERVAL: 2,
  FOCUS_AREAS: 3,
  OVERVIEW: 4,
  COMPLETION: 5,
  TOTAL: 5,
};

export interface ActionPlanSetupStepsProps {
  onComplete?: () => void;
  hasSurveyAccess?: boolean;
  hasActiveSurvey?: boolean;
  isLoading?: boolean;
}

export const ActionPlanSetupSteps = ({
  onComplete,
  hasSurveyAccess = true,
  hasActiveSurvey = false,
  isLoading = false,
}: ActionPlanSetupStepsProps) => {
  const { t } = useTranslation("actionPlanPage");
  const [searchParams] = useSearchParams();
  const skipIntro = searchParams.get("skipIntro") === "true";
  const showSurveyStep = hasSurveyAccess && !hasActiveSurvey; // Only show survey step if user can launch a survey and doesn't have an active one

  // Calculate initial step
  const getInitialStep = () => {
    if (!skipIntro) return STEPS.INTRO; // Show INTRO if not from dashboard
    if (!showSurveyStep) return STEPS.TRAINING_INTERVAL;
    return STEPS.INCLUSION_PULSE;
  };

  const [currentStep, setCurrentStep] = useState(getInitialStep());
  const [error, setError] = useState("");

  // Update initial step when hasActiveSurvey loads
  useEffect(() => {
    if (!isLoading) {
      const correctInitialStep = getInitialStep();
      if (currentStep <= STEPS.TRAINING_INTERVAL) {
        setCurrentStep(correctInitialStep);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, showSurveyStep, skipIntro]);

  // Preferences state
  const [daysUntilLaunch, setDaysUntilLaunch] = useState<string>("");
  const [modulesPerQuarter, setModulesPerQuarter] = useState<string>("");
  const [focusAreas, setFocusAreas] = useState<FocusArea[]>([]);

  const [suggestedActivities, setSuggestedActivities] = useState<
    Activity[] | undefined
  >(undefined);

  const actionPlanService = new ActionPlanService();

  const getActivitySuggestions = useMutation({
    mutationFn: (preferences: ActionPlanPreferences) =>
      actionPlanService.getActivitySuggestions(preferences),
  });

  const createActivities = useMutation({
    mutationFn: (activities: ActivityResponse[]) =>
      actionPlanService.createActivities(activities),
  });

  // Warn user about unsaved changes when on the journey step
  useEffect(() => {
    const hasUnsavedChanges =
      currentStep === STEPS.OVERVIEW &&
      suggestedActivities &&
      suggestedActivities.length > 0;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [currentStep, suggestedActivities]);

  const getNextStep = (current: number): number => {
    if (current === STEPS.INTRO && !showSurveyStep) {
      return STEPS.TRAINING_INTERVAL; // Skip INCLUSION_PULSE
    }
    return current + 1;
  };

  const getPreviousStep = (current: number): number => {
    if (current === STEPS.TRAINING_INTERVAL && !showSurveyStep) {
      return STEPS.INTRO; // Skip INCLUSION_PULSE
    }
    return current - 1;
  };

  const handleFocusAreaChange = (value: string) => {
    const focusArea = value as FocusArea;

    // If "Alda Recommended" is selected, deselect all others
    if (focusArea === FocusArea.AldaRecommended) {
      setFocusAreas([FocusArea.AldaRecommended]);
    } else {
      // Remove "Alda Recommended" if it was selected
      const newAreas = focusAreas.filter(
        (a) => a !== FocusArea.AldaRecommended
      );

      if (focusAreas.includes(focusArea)) {
        setFocusAreas(newAreas.filter((a) => a !== focusArea));
      } else {
        setFocusAreas([...newAreas, focusArea]);
      }
    }
  };

  const handleActivitiesAdd = async (activities: Activity[]) => {
    setSuggestedActivities((suggestedActivities ?? []).concat(activities));
  };

  const handleNextStep = async () => {
    setError("");
    if (currentStep === STEPS.FOCUS_AREAS) {
      // Call getActivitySuggestions when leaving step 3, i.e. after preferences are set
      try {
        const preferences: ActionPlanPreferences = {
          ...(!showSurveyStep
            ? {}
            : {
                survey_preference: {
                  days_until_launch: Number(daysUntilLaunch),
                },
              }),
          training_preference: {
            modules_per_quarter: Number(modulesPerQuarter),
            focus_areas: focusAreas,
          },
        };
        const suggestion = await getActivitySuggestions.mutateAsync(
          preferences
        );
        setSuggestedActivities(suggestion.map((a) => new Activity(a)));
        setCurrentStep(STEPS.OVERVIEW);
      } catch {
        setError(t("setup.errors.fetchSuggestions") || "");
      }
    } else {
      setCurrentStep(getNextStep(currentStep));
    }
  };

  const handleBackStep = () => {
    setError("");
    const previousStep = getPreviousStep(currentStep);
    const initialStep = getInitialStep();

    // Don't go back past the initial step
    if (previousStep < initialStep) {
      return;
    }
    setCurrentStep(previousStep);
  };

  const handleSaveActivities = async () => {
    setError("");
    await createActivities.mutateAsync(
      suggestedActivities?.map((a) => a.toAPI()) || [],
      {
        onSuccess: () => {
          setCurrentStep(STEPS.COMPLETION);
        },
        onError: () => {
          setError(t("setup.errors.save") || "");
        },
      }
    );
  };

  // Calculate progress accounting for skipped steps
  let totalSteps = STEPS.TOTAL;
  let effectiveStep = currentStep;

  // Adjust for skipped INTRO step
  if (skipIntro) {
    totalSteps -= 1;
    effectiveStep = currentStep > 0 ? currentStep - 1 : 0;
  }

  // Adjust for skipped INCLUSION_PULSE step
  if (!showSurveyStep) {
    totalSteps -= 1;
    if (currentStep >= STEPS.TRAINING_INTERVAL) {
      effectiveStep -= 1;
    }
  }

  const progress = (effectiveStep / totalSteps) * 100;

  const renderButtons = (
    onNext: () => void,
    nextDisabled: boolean,
    isLoading: boolean = false,
    nextLabel?: string
  ) => {
    const classes = classNames("ActionPlanSetupSteps__actions", {
      "ActionPlanSetupSteps__actions--right": currentStep === STEPS.OVERVIEW,
    });
    return (
      <>
        <div className={classes}>
          <Button
            variant="outlined"
            onClick={handleBackStep}
            disabled={
              currentStep === STEPS.INTRO ||
              (skipIntro && currentStep === STEPS.INCLUSION_PULSE) ||
              (skipIntro &&
                !showSurveyStep &&
                currentStep === STEPS.TRAINING_INTERVAL)
            }
          >
            {t("journey.back")}
          </Button>
          <Button
            variant="contained"
            color="info"
            onClick={onNext}
            disabled={nextDisabled || isLoading}
            loading={isLoading}
          >
            {nextLabel || t("journey.next")}
          </Button>
        </div>

        {error && (
          <Typography
            desktop="body2"
            color="error"
            className="ActionPlanSetupSteps__error"
          >
            {error}
          </Typography>
        )}
      </>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case STEPS.INTRO:
        return (
          <ActionPlanSetupBanner
            title={t("setup.intro.title")}
            description={t("setup.intro.description")}
            buttonLabel={t("setup.intro.button")}
            onClick={handleNextStep}
          />
        );

      case STEPS.INCLUSION_PULSE:
        return (
          <PreferenceStep
            title={t("setup.survey.frequencyTitle")}
            description={t("setup.survey.frequencyDescription")}
            optionsTitle={t("setup.survey.frequencyOptions.title")}
            options={getInclusionPulseOptions(t)}
            selectedValues={daysUntilLaunch ? [daysUntilLaunch] : []}
            onOptionChange={setDaysUntilLaunch}
            renderButtons={() =>
              renderButtons(handleNextStep, !daysUntilLaunch)
            }
          />
        );

      case STEPS.TRAINING_INTERVAL:
        return (
          <PreferenceStep
            title={t("setup.training.intervalTitle")}
            description={t("setup.training.intervalDescription")}
            optionsTitle={t("setup.training.intervalOptions.title")}
            options={getDeliveryIntervalOptions(t)}
            selectedValues={modulesPerQuarter ? [modulesPerQuarter] : []}
            onOptionChange={setModulesPerQuarter}
            renderButtons={() =>
              renderButtons(handleNextStep, !modulesPerQuarter)
            }
          />
        );

      case STEPS.FOCUS_AREAS:
        return (
          <PreferenceStep
            title={t("setup.training.focusAreaTitle")}
            description={t("setup.training.focusAreaDescription")}
            optionsTitle={t("setup.training.focusAreas.title")}
            options={getFocusAreaOptions(t)}
            selectedValues={focusAreas}
            onOptionChange={handleFocusAreaChange}
            renderButtons={() =>
              renderButtons(
                handleNextStep,
                focusAreas.length === 0,
                getActivitySuggestions.isLoading
              )
            }
          />
        );

      case STEPS.OVERVIEW:
        return (
          suggestedActivities && (
            <ActionPlanOverviewStep
              title={t("journey.title")}
              description={t("journey.description")}
              activities={suggestedActivities}
              hasSurveyAccess={hasSurveyAccess}
              setActivities={setSuggestedActivities}
              onActivitiesAdd={handleActivitiesAdd}
              renderButtons={() =>
                renderButtons(
                  handleSaveActivities,
                  false,
                  createActivities.isLoading,
                  t("journey.save")
                )
              }
            />
          )
        );

      case STEPS.COMPLETION:
        return (
          <ActionPlanSetupBanner
            title={t("completion.title")}
            buttonLabel={t("completion.button")}
            onClick={onComplete}
          />
        );

      default:
        return null;
    }
  };

  if (isLoading)
    return <Skeleton variant="rounded" width="100%" height={200} />;

  return (
    <Card className="ActionPlanSetupSteps">
      <ProgressBar progress={progress} />
      {!isLoading && renderStep()}
    </Card>
  );
};
